import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Button,
  Grid,
  Typography,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Breadcrumbs,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import Layout from "../../layout/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { config, projectMasterAPI, weeklyReportsAPI } from "../../config";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import * as XLSX from "xlsx/xlsx.mjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment";
import DescriptionIcon from "@mui/icons-material/Description";
import { MaterialReactTable } from "material-react-table";
import MuiLink from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { startOfWeek } from "date-fns";

function WeeklyPaymentReport() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const today = new Date();

  // Get Monday of current week
  const monday = startOfWeek(today, { weekStartsOn: 1 }); // weekStartsOn: 1 → Monday

  const [fromDate, setFromDate] = React.useState(monday);
  const [toDate, setToDate] = React.useState(today);
  const [totalCount, setTotalCount] = useState(0);
  const [projectID, setProjectID] = useState("");

  const [contractorTotal, setContractorTotal] = useState("");
  const [totalAmount, setTotalAmount] = useState({});
  const [ProjectNameList, setProjectNameList] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [exportExcel, setExportExcel] = useState(10);

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);

  let tempRefs2 = useRef(false);
  const downloadExcel = () => {
    // Map contractorTotal.data to desired format (remove labour_id and set headers)
    const formattedData = exportExcel.map((item) => ({
      "Labour Name": item.labour_name != null ? item.labour_name : "Rate Work",
      "Labour Type": item.labour_type_name,
      "Total Amount": item.total_line_amount,
    }));

    const workSheet = XLSX.utils.json_to_sheet(formattedData);
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, "Weekly Report");

    XLSX.writeFile(
      workBook,
      `KG-Weekly-payment-report-${moment().format("DD-MM-YYYY")}.xlsx`
    );
  };

  const getWeeklyReport = () => {
    const data = {
      labourID: id === undefined ? 0 : id,
      intProjectID: projectID,
      pageSize,
      pageIndex,
      FromDate: fromDate,
      ToDate: toDate,
      intType: 1,
      sorting,
      columnFilters,
    };
    config.method = "POST";
    config.data = JSON.stringify(data);
    config.url = weeklyReportsAPI;

    axios(config)
      .then(function (response) {
        setTotalCount(response.data.totalCount);
        setContractorTotal(response.data.data);
        setTotalAmount(response.data.tolAmt[0]?.total_line_total || 0);
        setExportExcel(response.data.excelExport);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    getWeeklyReport();
  }, [
    pageIndex,
    pageSize,
    fromDate,
    toDate,
    sorting,
    columnFilters,
    projectID,
  ]);

  useEffect(() => {
    if (id !== undefined) {
      getWeeklyReport();
    }
  }, [id]);

  useEffect(() => {
    if (!tempRefs2.current) {
      const data = { intType: 6 };

      config.method = "POST";
      config.data = JSON.stringify(data);
      config.url = projectMasterAPI;

      axios(config)
        .then(function (response) {
          setProjectNameList(response.data.result);
        })
        .catch(function (error) {
          console.log(error);
        });
      tempRefs2.current = true;
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "S.No.",
        Cell: ({ row, table }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          return pageIndex * pageSize + row.index + 1;
        },
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "labour_name",
        header: "Contractor Name",
        enableSorting: false,
        Cell: ({ row }) => {
          const labourId = row.original.labour_id;
          const queryParams = new URLSearchParams({
            from: fromDate?.toISOString?.(),
            to: toDate?.toISOString?.(),
            projectID: projectID || "",
          });

          const displayText =
            row.original.labour_name != null
              ? row.original.labour_name
              : "Rate Work";

          return (
            <span
              style={{
                color: "#1976d2",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() =>
                navigate(`/weeklyReport1/${labourId}?${queryParams.toString()}`)
              }
            >
              {displayText}
            </span>
          );
        },
      },
      {
        accessorKey: "labour_type_name",
        header: "Labour Type",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "total_line_amount",
        header: "Total Amount",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const amount = cell.getValue();
          return `₹ ${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
          }).format(amount)}`;
        },
      },
    ],
    [navigate, fromDate, toDate, projectID]
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Typography className="page_title" textAlign="left">
              Weekly Payment Report
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <MuiLink underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </MuiLink>
              <Typography color="text.primary">Reports</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            container
            item
            xs={12}
            lg={6}
            justifyContent="flex-end"
            spacing={2}
          >
            <Grid item>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
              >
                Back
              </LoadingButton>
            </Grid>
            <Grid item>
              <Button
                onClick={() => downloadExcel()}
                variant="contained"
                color="success"
                startIcon={<DescriptionIcon />}
                sx={{ borderRadius: 1 }}
              >
                Export to Excel
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ margin: "15px 0", padding: "10px 15px 0 0" }}
        >
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="select-project-label">Select Project</InputLabel>
              <Select
                labelId="select-project-label"
                id="select-project"
                value={projectID}
                label="Select Project"
                onChange={(e) => {
                  const selectprojectID = e.target.value;
                  setProjectID(selectprojectID);
                }}
              >
                {ProjectNameList.map((row, index) => (
                  <MenuItem value={row.id} key={index}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Select From Date"
                value={fromDate}
                inputFormat="DD/MM/YYYY"
                maxDate={toDate || undefined}
                onChange={(newValue) => {
                  setFromDate(newValue);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Select To Date"
                value={toDate}
                inputFormat="DD/MM/YYYY"
                minDate={fromDate || undefined}
                onChange={(newValue) => {
                  setToDate(newValue);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <LoadingButton
              size="large"
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => {
                setProjectID("");
              }}
            >
              clear
            </LoadingButton>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="text.primary"
            >
              Sum of All Contractor Payments:{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                ₹ {Number(totalAmount || 0).toLocaleString()}
              </Box>
            </Typography>
          </Grid>
        </Grid>

        <MaterialReactTable
          columns={columns}
          data={contractorTotal}
          enableGlobalFilter={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          className="mat-react-tbl"
          state={{
            isLoading: loading,
            pagination: { pageIndex, pageSize },
            sorting,
            columnFilters,
          }}
          manualPagination
          // initialState={{
          //   columnPinning: lgUp ? { right: ["present_employee", "ID"] } : {},
          // }}
          manualSorting
          manualFiltering
          rowCount={totalCount}
          onPaginationChange={(updater) => {
            const paginationState = updater({
              pageIndex,
              pageSize,
            });

            const { pageIndex: newPageIndex, pageSize: newPageSize } =
              paginationState;
            setPageIndex(newPageIndex);
            setPageSize(newPageSize);
          }}
          onSortingChange={setSorting}
          onColumnFiltersChange={setColumnFilters}
          muiTableBodyCellProps={{
            sx: {
              padding: "3px 8px",
              border: "1px solid #ccc",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              "& input": {
                color: "#fff !important",
                "&::placeholder": {
                  color: "#fff !important",
                  opacity: 1,
                },
              },
              "& .MuiSelect-select": {
                color: "#fff !important", // selected text color
              },
              "& .MuiSelect-icon": {
                color: "#fff !important", // dropdown arrow color
              },
              color: "#fff",
              fontWeight: "bold",
              "& svg": {
                color: "#fff",
              },
              "& .MuiTableSortLabel-root": {
                color: "#495057",
                "&.Mui-active": {
                  color: "#fff",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              borderBottom: "1px solid #ccc",
              backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            },
          })}
          muiTableHeadCellFilterTextFieldProps={{
            sx: {
              "& input": {
                color: "#fff !important", // Change text color
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important", // Change underline color
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important", // Hover underline color
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important", // Focused underline color
              },
            },
          }}
        />
      </Grid>
    </Layout>
  );
}

export default WeeklyPaymentReport;
